// ── Employee Performance widget: Copy and Export CSV ───────────────────
//
// The title bar's buttons hand over the whole table, not just what the card has room for: every
// employee (a Top-N limit only trims the card), in the columns on screen, in their order and in the
// current sort, then a totals row. Cells hold bare numbers — no dollar signs, thousands separators or
// % signs — with the unit in the heading instead, so a spreadsheet can add them up. Everything but
// downloadCsv is pure.

import {
    employeeColumnValue,
    sortEmployees,
    type EmployeeColumnContext,
    type EmployeeColumnDef,
    type EmployeeColumnFormat,
    type EmployeeColumnKey,
    type EmployeePerformanceData,
    type EmployeePerformancePeriod,
    type SortDir,
} from './employeePerformance'

export type ExportCell = string | number | null

export interface EmployeeExportTable {
    headers: string[]
    /** Decimal places each column's numbers are written with; null for a text column. */
    decimals: (number | null)[]
    /** One row per employee in sort order, then the totals row. A null cell is left empty. */
    rows: ExportCell[][]
}

const DECIMALS: Record<EmployeeColumnFormat, number | null> = {
    text: null, currency: 2, currencyCents: 2, int: 0, decimal: 2, percent: 2,
}

/** A column's number as it is written out: a percentage as 9.09, not 0.0909 (its heading says %). */
function exportNumber(def: EmployeeColumnDef, value: number | null): number | null {
    if (value === null || !Number.isFinite(value)) return null
    return def.format === 'percent' ? value * 100 : value
}

/**
 * The table Copy and Export CSV write: `columns` as the widget shows them, every employee sorted by
 * `sort`, and a totals row. Manual grants that weren't asked for stay empty cells, never zeros.
 */
export function buildEmployeeExport(
    data: EmployeePerformanceData,
    columns: readonly EmployeeColumnDef[],
    sort: { key: EmployeeColumnKey; dir: SortDir },
    ctx: EmployeeColumnContext,
): EmployeeExportTable {
    const sorted = sortEmployees(data.employees, sort.key, sort.dir, data.totals)
    const rows: ExportCell[][] = sorted.map((row, i) => columns.map(def => {
        if (def.key === 'rank') return i + 1
        if (def.key === 'employee') return row.name.trim() || 'Unknown employee'
        return exportNumber(def, employeeColumnValue(def.key, row, data.totals))
    }))
    const totals: ExportCell[] = columns.map(def => {
        if (def.key === 'rank') return null
        if (def.key === 'employee') return 'Total'
        return exportNumber(def, employeeColumnValue(def.key, data.totals, data.totals))
    })
    return {
        headers: columns.map(def => def.exportHeader(ctx)),
        decimals: columns.map(def => (def.key === 'rank' ? 0 : DECIMALS[def.format])),
        rows: [...rows, totals],
    }
}

/**
 * A cell a spreadsheet would run as a formula — one starting =, +, -, @, a tab or a return — is
 * written with a leading apostrophe. Names come from the till's employee records, not from us.
 */
const FORMULA_START = /^[=+\-@\t\r]/

function textCell(value: string, sep: string): string {
    const safe = FORMULA_START.test(value) ? `'${value}` : value
    return safe.includes(sep) || /["\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

function numberCell(value: number, decimals: number): string {
    const fixed = value.toFixed(decimals)
    return /^-0(?:\.0+)?$/.test(fixed) ? fixed.slice(1) : fixed   // -0.004 is 0.00, not -0.00
}

export function toDelimited(table: EmployeeExportTable, sep: string, eol: string): string {
    const line = (cells: ExportCell[]) => cells
        .map((cell, i) => {
            if (cell === null) return ''
            if (typeof cell === 'number') return numberCell(cell, table.decimals[i] ?? 2)
            return textCell(cell, sep)
        })
        .join(sep)
    return [table.headers.map(h => textCell(h, sep)).join(sep), ...table.rows.map(line)].join(eol)
}

/** For the clipboard: tab-separated, which a spreadsheet pastes straight into columns. */
export const toTsv = (table: EmployeeExportTable) => toDelimited(table, '\t', '\n')

/** For the file: comma-separated with CRLF line ends (RFC 4180). */
export const toCsv = (table: EmployeeExportTable) => toDelimited(table, ',', '\r\n')

// ── The file name ──────────────────────────────────────────────────────

export interface EmployeeExportNaming {
    /** The dashboard, by its period; absent when the widget wasn't told. */
    period?: EmployeePerformancePeriod
    /** The window the figures cover, as the server reported it (inclusive yyyy-MM-dd). */
    startDate: string
    endDate: string
    /** The store selector's wording: 'Calgary', 'My Store Group', 'All Stores'. */
    storeLabel: string
    /** 'all' | 'instore' | 'online'. */
    channel: string
}

const DASHBOARD_NAME: Record<EmployeePerformancePeriod, string> = { daily: 'daily', monthly: 'monthly', custom: 'custom-range' }

/** Lower-case ASCII words joined by hyphens: 'Saint-Jérôme – Centre' → 'saint-jerome-centre'. */
export function slugify(text: string): string {
    return text
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
}

const isWholeMonth = (start: string, end: string) => {
    if (!start.endsWith('-01') || start.slice(0, 7) !== end.slice(0, 7)) return false
    const [y, m] = start.split('-').map(Number)
    return Number(end.slice(8, 10)) === new Date(y, m, 0).getDate()
}

/**
 * e.g. employee-performance_daily_2026-02-27_calgary.csv,
 * employee-performance_monthly_2026-02_all-stores_online.csv, or for a month still under way
 * employee-performance_monthly_2026-09-01_to_2026-09-13_my-store-group.csv.
 */
export function employeeExportFilename(n: EmployeeExportNaming): string {
    const when = n.startDate === n.endDate
        ? n.startDate
        : n.period === 'monthly' && isWholeMonth(n.startDate, n.endDate)
            ? n.startDate.slice(0, 7)
            : `${n.startDate}_to_${n.endDate}`
    const channel = n.channel === 'instore' ? 'in-store' : n.channel === 'online' ? 'online' : null
    return ['employee-performance', n.period ? DASHBOARD_NAME[n.period] : null, when, slugify(n.storeLabel) || 'stores', channel]
        .filter(Boolean)
        .join('_') + '.csv'
}

/** Hands the CSV to the browser as a download. The BOM lets Excel read accented names as UTF-8. */
export function downloadCsv(filename: string, csv: string): void {
    const url = URL.createObjectURL(new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 0)
}
